import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScannerSection } from '../scanner';
import { performOCR } from '../../services/visionAPI';
import { previewLabel } from '../../services/devAPI';
import ReprintLabelPanel from './ReprintLabelPanel';
import PrintEventHandlingModal from './PrintEventHandlingModal';
import ModalBackButton from '../shared/ModalBackButton';
import { printRunProgressMessage, usePrintOrderRun, type PrintRunProgress } from '../../hooks/usePrintOrderRun';
import { useResumePrintEventScan } from '../../hooks/useResumePrintEventScan';
import type { PrintOrderWithSheetsResponse } from '../../schemas/apiResponse.schemas';
import {
  aggregatePrintJobIds,
  flattenPrintOrderItems,
  formatPrintJobBannerCounts,
  isPrintEventHandlingKind,
} from '@/shared/utils/printJobIds';
import { useAuth } from '../../contexts/AuthContext';
import ProductNameSearchModal from '../shared/ProductNameSearchModal';
import SkuAutocompleteInput from '../shared/SkuAutocompleteInput';
import { useSkuAutocomplete } from '../../hooks/useSkuAutocomplete';
import { useSkuNameCatalog } from '../../hooks/useSkuNameCatalog';
import { useSkuValidation } from '../../hooks/useSkuValidation';
import { useShelfValidation } from '../../hooks/useShelfValidation';
import { useClickOutside } from '../../hooks/useClickOutside';
import ShelfAutocompleteInput from '../shared/ShelfAutocompleteInput';
import { SHELF_RANGE, SKU_PREFIX } from '../../constants/app.constants';
import { getApiErrorMessage } from '../../services/errorUtils';
import { MESSAGE_TONES, statusSlotMessageClass } from '../../hooks/useStatusMessage';
import { navbarTitleForPathname } from '../../utils/pageTitle';
import {
  SCAN_LABEL_PREFIX_QUERY,
  SCAN_LABEL_REPRINT_TYPE,
  parseStorageIdPrefixParam,
} from '../../utils/scanLabelHandoff';
import type { SkuNameItem } from '../../services/SKUListAPI';
import type { LabelType } from '@/shared/types/label.types';
import {
  DESTINATION_PRINTERS,
  DEFAULT_PRINTER_ID,
  type DestinationPrinterId,
} from '@/shared/types/printer.types';
import type {
  CreatePrintOrderInput,
  PrintOrderSource,
  PrintOrderUnitInput,
} from '@/shared/types/printOrder.types';
import './PrintBoxLabel.css';

/** UI print modes include plain shelf labels and reprints; warehouse label APIs only accept {@link LabelType}. */
type PrintFlowLabelKind = LabelType | 'shelf' | 'reprint';

function toWarehouseLabelType(v: string | undefined | null): LabelType {
  return v === 'pick bin' ? 'pick bin' : 'stock';
}

function isWarehouseSkuLabelType(type: string): boolean {
  return type === 'stock' || type === 'pick-bin';
}

interface StashedProductCriteria {
  readonly sku: string;
  readonly name: string;
}

interface LabelTypeConfig {
  title: string;
  skuRequired: boolean;
  shelfRequired: boolean;
  shelfOptional: boolean;
  type: PrintFlowLabelKind;
  showBaseQty: boolean;
}

const PRINT_LABEL_MODES: Record<string, LabelTypeConfig> = {
  stock: {
    title: 'Print Warehouse Labels',
    skuRequired: true,
    shelfRequired: false,
    shelfOptional: true,
    type: 'stock',
    showBaseQty: true,
  },
  'pick-bin': {
    title: 'Print Warehouse Labels',
    skuRequired: true,
    shelfRequired: false,
    shelfOptional: true,
    type: 'pick bin',
    showBaseQty: false,
  },
  reprint: {
    title: 'Print Warehouse Labels',
    skuRequired: false,
    shelfRequired: false,
    shelfOptional: false,
    type: 'reprint',
    showBaseQty: false,
  },
  shelf: {
    title: 'Print Warehouse Labels',
    skuRequired: false,
    shelfRequired: false,
    shelfOptional: false,
    type: 'shelf',
    showBaseQty: false,
  },
};

/** Label types offered in the selector, in display order. */
const LABEL_TYPE_OPTIONS: ReadonlyArray<{ readonly value: string; readonly label: string }> = [
  { value: 'pick-bin', label: 'Pick Bin' },
  { value: 'stock', label: 'Stock' },
  { value: 'reprint', label: 'Reprint' },
  { value: 'shelf', label: 'Shelf' },
];

interface PrintBoxLabelProps {
  initialSku?: string;
  /** Pre-selects a type; the Label Type selector stays visible so this matches the print page. */
  initialLabelType?: string;
  initialQuantity?: number;
  initialBaseQty?: number;
  initialShelfIndex?: string;
  /** Where the print was triggered from — recorded on every print order. */
  source?: PrintOrderSource;
  /** Overlay layout only (receiving / shelf-audit). Print UI and flows are the same as the page. */
  embedded?: boolean;
}

interface BatchItem {
  sku?: string;
  shelfIndex: string | null;
  labelType: string;
}

const UNIQUE_IDS_MIN = 1;
const UNIQUE_IDS_MAX = 30;
const SHELF_LABEL_BATCH_MAX = 6;
const SHELF_POSITIONS_PER_COLUMN = SHELF_RANGE.POSITIONS_1_TO_6.length;
const SHELF_LETTERS_PER_AISLE = SHELF_RANGE.LETTERS_A_TO_Z.length;

type ShelfModalTarget = 'start' | 'end';

interface ParsedShelfParts {
  aisle: number;
  letter: string;
  position: number;
}

function parseShelfParts(shelf: string): ParsedShelfParts | null {
  const parts = String(shelf).trim().toUpperCase().split('-');
  if (parts.length !== 3) return null;
  const aisle = parseInt(parts[0], 10);
  const letter = parts[1];
  const position = parseInt(parts[2], 10);
  if (!Number.isFinite(aisle) || aisle < 1) return null;
  if (!/^[A-Z]$/.test(letter)) return null;
  if (!Number.isFinite(position) || position < 1 || position > SHELF_POSITIONS_PER_COLUMN) {
    return null;
  }
  return { aisle, letter, position };
}

function isValidShelfLabelIndex(shelf: string): boolean {
  return parseShelfParts(shelf) != null;
}

/** Inclusive geometric range of shelf labels (aisle × A–Z × positions 1–6). */
function expandShelfLabelRange(start: string, end: string): string[] | { error: string } {
  const a = parseShelfParts(start);
  const b = parseShelfParts(end);
  if (!a || !b) {
    return { error: 'Shelf index must be in format number-letter-number (e.g. 5-A-1).' };
  }

  const toLinear = (p: ParsedShelfParts): number =>
    ((p.aisle - 1) * SHELF_LETTERS_PER_AISLE + (p.letter.charCodeAt(0) - 65)) * SHELF_POSITIONS_PER_COLUMN
    + (p.position - 1);

  const fromLinear = (n: number): string => {
    const position = (n % SHELF_POSITIONS_PER_COLUMN) + 1;
    const rest = Math.floor(n / SHELF_POSITIONS_PER_COLUMN);
    const letter = String.fromCharCode(65 + (rest % SHELF_LETTERS_PER_AISLE));
    const aisle = Math.floor(rest / SHELF_LETTERS_PER_AISLE) + 1;
    return `${aisle}-${letter}-${position}`;
  };

  const startLin = toLinear(a);
  const endLin = toLinear(b);
  if (startLin > endLin) {
    return { error: 'Start shelf must be earlier than or equal to end shelf.' };
  }

  const out: string[] = [];
  for (let i = startLin; i <= endLin; i++) {
    out.push(fromLinear(i));
  }
  return out;
}

/** Base qty is a free-text field; the print order stores it as an integer. */
function parseBaseQty(value: string | null | undefined): number | null {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') return null;
  const parsed = parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function clampUniqueIds(n: number): number {
  return Math.min(UNIQUE_IDS_MAX, Math.max(UNIQUE_IDS_MIN, Math.floor(Number(n)) || UNIQUE_IDS_MIN));
}

/** While the field is focused with an empty draft, treat as 1 until blur commits. */
function effectiveUniqueIdCount(committed: number, inputFocused: boolean, draft: string): number {
  if (!inputFocused) {
    return clampUniqueIds(committed);
  }
  const trimmed = draft.trim();
  if (trimmed === '') {
    return UNIQUE_IDS_MIN;
  }
  const v = parseInt(trimmed, 10);
  if (Number.isNaN(v)) {
    return UNIQUE_IDS_MIN;
  }
  return clampUniqueIds(v);
}

function formatUniqueIdDisplay(
  count: number,
  focused: boolean,
  draft: string,
): string {
  if (focused) return draft;
  if (count >= UNIQUE_IDS_MAX) return `${UNIQUE_IDS_MAX} (Max)`;
  return String(count);
}

/** Map the print page's error flag onto the shared reserved-slot classes. */
function printStatusSlotClass(isError: boolean, invalidInput: string | null, text: string): string {
  if (isError || invalidInput) {
    return statusSlotMessageClass(MESSAGE_TONES.ERROR);
  }
  if (/^(Printing |Processing |Retrying )/i.test(text)) {
    return statusSlotMessageClass(MESSAGE_TONES.INFO);
  }
  return statusSlotMessageClass(MESSAGE_TONES.SUCCESS);
}

interface InputsRefState {
  SKU: string;
  shelfIndex: string;
  endShelfIndex: string;
  shelfNumber: string;
  shelfLetter: string;
  shelfPosition: string;
  labelConfig: LabelTypeConfig | null;
  baseQty: string;
  printBatch: BatchItem[];
  printLabelCount: number;
  printBigLabelCount: number;
  uniqueIdCount: number;
}

function PrintBoxLabel({
  initialSku = '',
  initialLabelType = '',
  initialQuantity = 1,
  initialBaseQty,
  initialShelfIndex = '',
  source = 'print_page',
  embedded = false,
}: PrintBoxLabelProps) {
  const [searchParams] = useSearchParams();
  const reprintHandoffAppliedRef = useRef(false);
  const queryReprintPrefix = !embedded
    ? parseStorageIdPrefixParam(searchParams.get(SCAN_LABEL_PREFIX_QUERY))
    : null;
  const queryIsReprint = !embedded && searchParams.get('type') === SCAN_LABEL_REPRINT_TYPE;
  const { isDev } = useAuth();
  const { runPrintOrder } = usePrintOrderRun();
  const [lastPrintJob, setLastPrintJob] = useState<PrintOrderWithSheetsResponse | null>(null);
  const [printEventHandlingOpen, setPrintEventHandlingOpen] = useState(false);
  const [resumedScannedPrefix, setResumedScannedPrefix] = useState<string | null>(null);
  const printEventResume = useResumePrintEventScan();

  useEffect(() => {
    if (!printEventResume.job) return;
    setLastPrintJob(printEventResume.job);
    setPrintEventHandlingOpen(true);
    setResumedScannedPrefix(printEventResume.scannedPrefix);
  }, [printEventResume.job, printEventResume.scannedPrefix]);
  const [selectedType, setSelectedType] = useState<string>(
    initialLabelType || (queryIsReprint ? SCAN_LABEL_REPRINT_TYPE : ''),
  );
  const [printerId, setPrinterId] = useState<DestinationPrinterId>(DEFAULT_PRINTER_ID);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const labelConfig = selectedType ? PRINT_LABEL_MODES[selectedType] : null;
  const isWarehouseSkuPrintType = isWarehouseSkuLabelType(selectedType);
  // Hold origin shelf in memory; inject into the field only after a label type is selected.
  const pendingShelfIndexRef = useRef(initialShelfIndex || '');
  const [shelfIndex, setShelfIndex] = useState(
    initialLabelType && initialShelfIndex ? initialShelfIndex : '',
  );
  const [endShelfIndex, setEndShelfIndex] = useState('');
  const [shelfModalTarget, setShelfModalTarget] = useState<ShelfModalTarget>('start');
  const [SKU, setSKU] = useState(initialSku || '');
  const [productName, setProductName] = useState('');
  const [, setIsReadyForSubmit] = useState(false);
  const [message, setMessage] = useState('');
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [errorInvalidInput, setErrorInvalidInput] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showManualShelfInput, setShowManualShelfInput] = useState(false);
  const [shelfModalError, setShelfModalError] = useState<string | null>(null);
  const [showManualSKUInput, setShowManualSKUInput] = useState(false);
  const [showManualNameInput, setShowManualNameInput] = useState(false);
  const [printBatch, setPrintBatch] = useState<BatchItem[]>([]);
  const [lastAddedSku, setLastAddedSku] = useState<string | null>(null);
  const [manualSKU, setManualSKU] = useState(SKU_PREFIX);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef<boolean>(false);
  const {
    allSkus: allSKUs,
    loading: skusLoading,
    error: skuLoadError,
    validateSku,
    findBestSkuMatch,
  } = useSkuValidation();
  const {
    loading: shelvesLoading,
    error: shelfLoadError,
    validateShelf,
    normalizeShelf,
  } = useShelfValidation();
  const { resolveNameForSku, rememberName } = useSkuNameCatalog();
  const {
    filteredSkus: filteredSKUs,
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
  } = useSkuAutocomplete({
    value: manualSKU,
    allSkus: allSKUs,
    prefix: SKU_PREFIX,
    skipAutocompleteOpenRef,
    inputRef: skuInputRef,
    autocompleteRef,
  });

  const applyProductSku = useCallback(
    (sku: string, name?: string): void => {
      setSKU(sku);
      const resolved = name ?? resolveNameForSku(sku);
      setProductName(resolved);
      if (name) {
        rememberName(sku, name);
      }
    },
    [resolveNameForSku, rememberName],
  );

  /** SKU/Name held while user is on Shelf or Reprint so Stock/Pick Bin can restore them. */
  const stashedProductCriteriaRef = useRef<StashedProductCriteria | null>(null);

  const clearProductCriteria = useCallback((options?: { readonly clearStash?: boolean }): void => {
    if (options?.clearStash !== false) {
      stashedProductCriteriaRef.current = null;
    }
    setSKU('');
    setProductName('');
  }, []);

  const [shelfNumber, setShelfNumber] = useState('');
  const [shelfLetter, setShelfLetter] = useState('');
  const [shelfPosition, setShelfPosition] = useState('');
  const [manualShelfText, setManualShelfText] = useState('');

  const [baseQty, setBaseQty] = useState('');

  const [uniqueIdCount, setUniqueIdCount] = useState(UNIQUE_IDS_MIN);
  const [uniqueIdInputFocused, setUniqueIdInputFocused] = useState(false);
  const [uniqueIdInputStr, setUniqueIdInputStr] = useState('');

  const [printLabelCount, setPrintLabelCount] = useState(initialQuantity || 1);
  const [printBigLabelCount, setPrintBigLabelCount] = useState(1);

  const [showLabelPreviewModal, setShowLabelPreviewModal] = useState(false);
  const [labelPreviewTitle, setLabelPreviewTitle] = useState('');
  const [labelPreviewImages, setLabelPreviewImages] = useState<string[]>([]);
  const [labelPreviewLoading, setLabelPreviewLoading] = useState(false);
  const [labelPreviewError, setLabelPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (initialSku) {
      applyProductSku(initialSku);
    }
  }, [initialSku, applyProductSku]);

  useEffect(() => {
    pendingShelfIndexRef.current = initialShelfIndex || '';
    // Type already chosen (e.g. receiving): inject immediately. Otherwise wait for selection.
    if (initialLabelType && initialShelfIndex) {
      setShelfIndex(initialShelfIndex);
    }
  }, [initialShelfIndex, initialLabelType]);

  // When the name catalog loads after a SKU was already selected, fill Name.
  useEffect(() => {
    if (!SKU || productName) return;
    const name = resolveNameForSku(SKU);
    if (name) {
      setProductName(name);
    }
  }, [SKU, productName, resolveNameForSku]);

  useEffect(() => {
    if (initialLabelType) {
      setSelectedType(initialLabelType);
    }
  }, [initialLabelType]);

  useEffect(() => {
    if (embedded || reprintHandoffAppliedRef.current || !queryIsReprint) return;
    reprintHandoffAppliedRef.current = true;
    setSelectedType(SCAN_LABEL_REPRINT_TYPE);
  }, [embedded, queryIsReprint]);

  useEffect(() => {
    if (initialQuantity && initialQuantity > 0) {
      setPrintLabelCount(initialQuantity);
    }
  }, [initialQuantity]);

  useEffect(() => {
    if (initialBaseQty !== undefined && initialBaseQty > 0) {
      setBaseQty(String(initialBaseQty));
    }
  }, [initialBaseQty]);

  const [showPrintAisleModal, setShowPrintAisleModal] = useState(false);
  const [aisleLetterStart, setAisleLetterStart] = useState('');
  const [aisleLetterEnd, setAisleLetterEnd] = useState('');
  const [aisleRangeError, setAisleRangeError] = useState('');

  // useRef initial value cast: the ref is immediately populated by the
  // useEffect below on every render, so the empty object is never read.
  // A null initial + null checks would add noise to every consumer.
  const inputsRef = useRef<InputsRefState>({} as InputsRefState);
  const effectiveUniqueIdCountValue = effectiveUniqueIdCount(
    uniqueIdCount,
    uniqueIdInputFocused,
    uniqueIdInputStr,
  );
  useEffect(() => {
    inputsRef.current = {
      SKU,
      shelfIndex,
      endShelfIndex,
      shelfNumber,
      shelfLetter,
      shelfPosition,
      labelConfig,
      baseQty,
      printBatch,
      printLabelCount,
      printBigLabelCount,
      uniqueIdCount: effectiveUniqueIdCountValue,
    };
  });

  useEffect(() => {
    if (skuLoadError) {
      console.error('Error loading SKUs:', skuLoadError);
      setMessage('Error loading product database. Please refresh the page.');
    }
  }, [skuLoadError]);

  useEffect(() => {
    if (shelfLoadError) {
      console.error('Error loading shelf names:', shelfLoadError);
      setMessage('Error loading shelf database. Please refresh the page.');
    }
  }, [shelfLoadError]);

  /** Committed value 1, unfocused field — also used when clearing label type. */
  const resetUniqueIdsToDefault = (): void => {
    setUniqueIdCount(UNIQUE_IDS_MIN);
    setUniqueIdInputFocused(false);
    setUniqueIdInputStr('');
  };

  /** Switch label type, clearing everything the previous type owned. */
  const selectLabelType = (next: string): void => {
    if (selectedType !== next) {
      const prevRequiresSku =
        selectedType !== '' && PRINT_LABEL_MODES[selectedType]?.skuRequired === true;
      const nextRequiresSku = PRINT_LABEL_MODES[next]?.skuRequired === true;

      if (!nextRequiresSku) {
        const trimmedSku = SKU.trim();
        if (trimmedSku) {
          stashedProductCriteriaRef.current = { sku: trimmedSku, name: productName };
        }
        clearProductCriteria({ clearStash: false });
      } else if (!prevRequiresSku) {
        const stashed = stashedProductCriteriaRef.current;
        if (stashed?.sku) {
          applyProductSku(stashed.sku, stashed.name || undefined);
          stashedProductCriteriaRef.current = null;
        }
      }

      setBaseQty('');
      resetUniqueIdsToDefault();
      setPrintBatch([]);
      setEndShelfIndex('');
    }
    setSelectedType(next);
    setShelfIndex(pendingShelfIndexRef.current);
    setShowTypeSelector(false);
  };

  const prevSKURef = useRef(SKU);
  useEffect(() => {
    if (prevSKURef.current !== '' && SKU !== '' && prevSKURef.current !== SKU) {
      setPrintBatch([]);
    }
    prevSKURef.current = SKU;
  }, [SKU]);

  useEffect(() => {
    if (SKU && SKU !== lastAddedSku) {
      setLastAddedSku(null);
    }
  }, [SKU, lastAddedSku]);

  useEffect(() => {
    if (printBatch.length === 0) {
      setLastAddedSku(null);
    }
  }, [printBatch.length]);

  useEffect(() => {
    if (!labelConfig) return;
    if (selectedType === 'shelf') {
      setIsReadyForSubmit(false);
      return;
    }
    const skuReady = labelConfig.skuRequired ? SKU.length > 0 : true;
    const shelfReady = labelConfig.shelfRequired ? shelfIndex.length > 0 : true;
    const allItemsReady = skuReady && shelfReady;
    setIsReadyForSubmit(allItemsReady);
  }, [SKU, shelfIndex, labelConfig, selectedType]);

  const hasInputForPrint = labelConfig && selectedType !== 'shelf' && (
    (labelConfig.skuRequired ? SKU.length > 0 : true) &&
    (labelConfig.shelfRequired ? shelfIndex.length > 0 : true)
  );

  const shelfRangeMode =
    selectedType === 'shelf'
    && isValidShelfLabelIndex(shelfIndex)
    && isValidShelfLabelIndex(endShelfIndex);
  const shelfRangeExpanded = shelfRangeMode
    ? expandShelfLabelRange(shelfIndex, endShelfIndex)
    : null;
  const shelfRangeError =
    shelfRangeExpanded != null && !Array.isArray(shelfRangeExpanded)
      ? shelfRangeExpanded.error
      : null;

  const validateShelfIndex = (shelfIdx: string): boolean => {
    if (shelvesLoading) {
      return false;
    }
    return validateShelf(shelfIdx);
  };

  const matchShelfIndex = (ocrText: string): string | null => {
    const shelfPattern = /\d{1,2}-[A-Za-z]-\d+/;
    const shelfMatch = ocrText.match(shelfPattern);
    if (shelfMatch) {
      return shelfMatch[0];
    } else {
      return null;
    }
  };

  const matchSKU = (ocrText: string): string | null => {
    if (skusLoading) {
      return null;
    }
    return findBestSkuMatch(ocrText);
  };

  const validateSKU = (sku: string): boolean => {
    if (skusLoading) {
      return false;
    }
    return validateSku(sku);
  };

  const handleCapture = async (imageSrc: string | null): Promise<boolean> => {
    if (!labelConfig || !imageSrc) return false;
    setMessage('Processing image...');
    const base64Image = imageSrc;
    const ocrText_raw = await performOCR(base64Image);

    if (ocrText_raw) {
      const ocrText = cleanText(ocrText_raw);
      const shouldMatchSku = labelConfig.skuRequired;
      const matchedSKU = shouldMatchSku ? matchSKU(ocrText) : null;
      const matchedShelfIndex = (labelConfig.shelfRequired || labelConfig.shelfOptional) ? matchShelfIndex(ocrText) : null;
      
      if (matchedSKU && matchedShelfIndex) {
        const skuValid = validateSKU(matchedSKU);
        const normalizedShelf = normalizeShelf(matchedShelfIndex);
        const shelfValid = validateShelfIndex(normalizedShelf);
        
        if (skuValid && shelfValid) {
          applyProductSku(matchedSKU);
          setShelfIndex(normalizedShelf);
          setMessage(`Matched SKU ${matchedSKU} and shelf ${normalizedShelf}.`);
          setErrorInvalidInput(null);
          return true;
        }
        if (!skuValid) {
          setMessage(`SKU ${matchedSKU} Not found in Product DB`);
          setErrorInvalidInput(matchedSKU);
          return false;
        }
        setMessage(`Shelf ${normalizedShelf} does not exist`);
        setErrorInvalidInput(normalizedShelf);
        return false;
      }
      if (matchedShelfIndex && (labelConfig.shelfRequired || labelConfig.shelfOptional)) {
        const normalizedShelf = normalizeShelf(matchedShelfIndex);
        if (validateShelfIndex(normalizedShelf)) {
          setMessage(`Matched shelf index ${normalizedShelf}.`);
          setShelfIndex(normalizedShelf);
          setErrorInvalidInput(null);
          return true;
        }
        setMessage(`Shelf ${normalizedShelf} does not exist`);
        setErrorInvalidInput(normalizedShelf);
        return false;
      }
      if (matchedSKU && shouldMatchSku) {
        if (validateSKU(matchedSKU)) {
          setMessage(`Matched SKU ${matchedSKU}.`);
          applyProductSku(matchedSKU);
          setErrorInvalidInput(null);
          return true;
        }
        setMessage(`SKU ${matchedSKU} Not found in Product DB`);
        setErrorInvalidInput(matchedSKU);
        return false;
      }
      const requiredFields: string[] = [];
      if (shouldMatchSku) requiredFields.push('SKU');
      if (labelConfig.shelfRequired || labelConfig.shelfOptional) requiredFields.push('shelf');
      const matchPart = requiredFields.length > 0
        ? `Could not match ${requiredFields.join(' or ')}. `
        : 'Could not match any required fields. ';
      const msg = `${matchPart}Scan input: ${ocrText}`;
      setMessage(msg);
      setErrorInvalidInput(ocrText);
      return false;
    }
    setMessage('Error processing image. Please try again.');
    setErrorInvalidInput(null);
    return false;
  };

  const cleanText = (ocrText: string): string => {
    const removeSpace = ocrText.replace(/\s+/g, '').toUpperCase();
    const transform = transformString1toI(removeSpace);
    return transform;
  };

  function transformString1toI(input: string): string {
    const regex = /^(\d+)-1-(\d+)$/;
    const match = input.match(regex);

    if (match) {
      return `${match[1]}-I-${match[2]}`;
    } else {
      return input;
    }
  }

  const handleManualShelfInput = () => {
    let normalizedIndex: string;

    if (manualShelfText.trim()) {
      normalizedIndex = manualShelfText.trim().toUpperCase();
    } else if (shelfNumber && shelfLetter && shelfPosition) {
      normalizedIndex = `${shelfNumber}-${shelfLetter}-${shelfPosition}`.toUpperCase();
    } else {
      setMessage('Please select all three fields or enter a shelf name.');
      setErrorInvalidInput(null);
      setTimeout(() => setMessage(''), 2000);
      return;
    }

    if (selectedType !== 'shelf' && !validateShelfIndex(normalizedIndex)) {
      setShelfModalError(`Shelf ${normalizedIndex} does not exist`);
      setMessage(`Shelf ${normalizedIndex} does not exist`);
      setErrorInvalidInput(normalizedIndex);
      setTimeout(() => {
        setMessage('');
        setErrorInvalidInput(null);
      }, 5000);
      return;
    }

    if (selectedType === 'shelf' && !isValidShelfLabelIndex(normalizedIndex)) {
      setShelfModalError('Shelf index must be in format number-letter-number (e.g. 5-A-1).');
      setMessage('Shelf index must be in format number-letter-number (e.g. 5-A-1).');
      setErrorInvalidInput(normalizedIndex);
      setTimeout(() => {
        setMessage('');
        setErrorInvalidInput(null);
      }, 5000);
      return;
    }

    setShelfModalError(null);
    if (shelfModalTarget === 'end') {
      setEndShelfIndex(normalizedIndex);
      setPrintBatch([]);
      setMessage(`End shelf ${normalizedIndex} set successfully.`);
    } else {
      setShelfIndex(normalizedIndex);
      setMessage(`Shelf index ${normalizedIndex} set successfully.`);
    }
    setErrorInvalidInput(null);
    setShowManualShelfInput(false);

    setShelfNumber('');
    setShelfLetter('');
    setShelfPosition('');
    setManualShelfText('');

    setTimeout(() => setMessage(''), 2000);
  };

  const handleManualSKUInputClose = () => {
    setShowManualSKUInput(false);
    setManualSKU(SKU_PREFIX);
  };

  const handleManualShelfInputClose = () => {
    setShowManualShelfInput(false);
    setShelfModalError(null);
    setShelfModalTarget('start');
    setShelfNumber('');
    setShelfLetter('');
    setShelfPosition('');
    setManualShelfText('');
  };

  const openShelfModal = (target: ShelfModalTarget): void => {
    setShelfModalTarget(target);
    setShowManualShelfInput(true);
    setShowManualSKUInput(false);
    setShowManualNameInput(false);
    const source = target === 'end' ? endShelfIndex : shelfIndex;
    if (source) {
      const parts = source.split('-');
      if (parts.length === 3) {
        setShelfNumber(parts[0]);
        setShelfLetter(parts[1]);
        setShelfPosition(parts[2]);
      }
    } else {
      setShelfNumber('');
      setShelfLetter('');
      setShelfPosition('');
    }
    setManualShelfText('');
  };

  const handleManualSKUInput = () => {
    if (manualSKU.trim() && manualSKU.trim().length > SKU_PREFIX.length) {
      const skuValue = manualSKU.trim().toUpperCase();
      
      if (!validateSKU(skuValue)) {
        setMessage(`SKU ${skuValue} Not found in Product DB`);
        setErrorInvalidInput(skuValue);
        setTimeout(() => {
          setMessage('');
          setErrorInvalidInput(null);
        }, 5000);
        return;
      }
      
      applyProductSku(skuValue);
      setMessage(`SKU ${skuValue} set successfully.`);
      handleManualSKUInputClose();
      setShowAutocomplete(false);

      setTimeout(() => setMessage(''), 2000);
    } else {
      setMessage(`Please enter a SKU.`);
      setTimeout(() => setMessage(''), 2000);
    }
  };

  const handleSKUInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length < SKU_PREFIX.length) {
      setManualSKU(SKU_PREFIX);
    } else if (!value.startsWith(SKU_PREFIX)) {
      setManualSKU(SKU_PREFIX + value.substring(SKU_PREFIX.length));
    } else {
      setManualSKU(value.toUpperCase());
    }
  };

  const handleSKUInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete || filteredSKUs.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < filteredSKUs.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectSKU(filteredSKUs[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const selectSKU = (sku: string) => {
    if (!validateSKU(sku)) {
      setMessage(`SKU ${sku} Not found in Product DB`);
      setErrorInvalidInput(sku);
      setTimeout(() => {
        setMessage('');
        setErrorInvalidInput(null);
      }, 5000);
      return;
    }
    skipAutocompleteOpenRef.current = true;
    applyProductSku(sku);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    setMessage(`SKU ${sku} set successfully.`);
    setErrorInvalidInput(null);
    handleManualSKUInputClose();
    setTimeout(() => setMessage(''), 2000);
  };

  const selectNameResult = (item: SkuNameItem): void => {
    applyProductSku(item.sku, item.name);
    setMessage(`Name set for ${item.sku}.`);
    setErrorInvalidInput(null);
    setShowManualNameInput(false);
    setTimeout(() => setMessage(''), 2000);
  };

  useClickOutside(
    [autocompleteRef, skuInputRef],
    () => setShowAutocomplete(false),
  );

  const getMaxBatchSize = () => selectedType === 'shelf' ? SHELF_LABEL_BATCH_MAX : 5;

  const handleAddToBatch = () => {
    const maxBatch = getMaxBatchSize();

    if (selectedType === 'shelf') {
      if (!shelfIndex) {
        setMessage('Shelf is required to add to batch.');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      if (printBatch.length >= maxBatch) {
        setMessage(`Batch is full (maximum ${maxBatch} labels). Please print the batch first.`);
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      const batchItem: BatchItem = { shelfIndex: shelfIndex, labelType: 'shelf' };
      setPrintBatch([...printBatch, batchItem]);
      setMessage(`Added shelf ${shelfIndex} to batch (${printBatch.length + 1}/${maxBatch})`);
    } else {
      if (!isWarehouseSkuPrintType) {
        setMessage('Select Stock or Pick Bin before adding to batch.');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      if (!SKU) {
        setMessage('SKU is required to add to batch.');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      if (printBatch.length >= maxBatch) {
        setMessage(`Batch is full (maximum ${maxBatch} labels). Please print the batch first.`);
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      const batchItem: BatchItem = { sku: SKU, shelfIndex: shelfIndex || null, labelType: labelConfig!.type };
      setPrintBatch([...printBatch, batchItem]);
      setMessage(`Added ${SKU} to batch (${printBatch.length + 1}/${maxBatch})`);
      setLastAddedSku(SKU);
      clearProductCriteria();
    }
    setTimeout(() => setMessage(''), 2000);
  };

  /** Shared progress text for every print order this page starts. */
  const reportPrintProgress = (progress: PrintRunProgress): void => {
    setMessage(printRunProgressMessage(progress));
  };

  /**
   * Run a print order to completion and surface the outcome.
   * A stopped order leaves its unprinted balance recoverable from Print Log.
   */
  const runPrint = async (
    input: CreatePrintOrderInput,
    startMessage: string,
    labelNoun = 'label',
  ): Promise<boolean> => {
    setIsSubmitting(true);
    setIsErrorMessage(false);
    setMessage(startMessage);

    const result = await runPrintOrder(input, { onProgress: reportPrintProgress, labelNoun, printerId });

    if (result.order && isPrintEventHandlingKind(result.order.order.labelKind)) {
      setResumedScannedPrefix(null);
      setLastPrintJob(result.order);
    }

    setIsErrorMessage(!result.completed);
    setMessage(result.message);
    setIsSubmitting(false);
    setTimeout(() => { setMessage(''); setIsErrorMessage(false); }, result.completed ? 3000 : 10000);
    return result.completed;
  };

  const printShelfUnits = async (shelfIndexes: readonly string[], startMessage: string): Promise<boolean> => {
    return runPrint(
      {
        labelKind: 'shelf',
        source,
        units: shelfIndexes.map((shelf) => ({ shelfIndex: shelf })),
      },
      startMessage,
      'shelf label',
    );
  };

  const handlePrintColumn = async () => {
    const { shelfIndex: currentShelfIndex } = inputsRef.current;
    if (!currentShelfIndex || isSubmitting) return;

    const parts = String(currentShelfIndex).trim().split('-');
    if (parts.length < 3) {
      setMessage('Shelf index must be in format number-letter-number (e.g. 5-A-1).');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const prefix = `${parts[0]}-${parts[1]}`;
    const columnShelves = SHELF_RANGE.POSITIONS_1_TO_6.map((n) => `${prefix}-${n}`);
    setPrintBatch([]);
    await printShelfUnits(columnShelves, `Printing column (${SHELF_LABEL_BATCH_MAX} shelf labels)...`);
  };

  const handlePrintShelfRange = async (): Promise<void> => {
    const {
      shelfIndex: currentShelfIndex,
      endShelfIndex: currentEndShelfIndex,
    } = inputsRef.current;
    if (!currentShelfIndex || !currentEndShelfIndex || isSubmitting) return;

    const rangeResult = expandShelfLabelRange(currentShelfIndex, currentEndShelfIndex);
    if (!Array.isArray(rangeResult)) {
      setIsErrorMessage(true);
      setMessage(rangeResult.error);
      setTimeout(() => { setMessage(''); setIsErrorMessage(false); }, 5000);
      return;
    }

    const printed = await printShelfUnits(
      rangeResult,
      `Printing ${rangeResult.length} shelf label${rangeResult.length !== 1 ? 's' : ''}...`,
    );
    if (printed) {
      setPrintBatch([]);
    }
  };

  const handleOpenPrintAisleModal = () => {
    setAisleLetterStart('');
    setAisleLetterEnd('');
    setAisleRangeError('');
    setShowPrintAisleModal(true);
  };

  const handleClosePrintAisleModal = () => {
    setShowPrintAisleModal(false);
    setAisleLetterStart('');
    setAisleLetterEnd('');
    setAisleRangeError('');
  };

  const handleCloseLabelPreviewModal = () => {
    setShowLabelPreviewModal(false);
    setLabelPreviewImages([]);
    setLabelPreviewError(null);
    setLabelPreviewLoading(false);
    setLabelPreviewTitle('');
  };

  const handlePreviewWarehouseLabel = async () => {
    const { SKU: currentSKU, printBatch: currentBatch } = inputsRef.current;
    const items =
      currentBatch.length > 0
        ? currentBatch
            .map((item) => item.sku)
            .filter((sku): sku is string => typeof sku === 'string' && sku.length > 0)
            .map((sku) => ({ sku }))
        : currentSKU
          ? [{ sku: currentSKU }]
          : [];

    if (items.length === 0 || labelPreviewLoading) {
      return;
    }

    const title = currentBatch.length > 0 ? 'Preview Print Batch' : 'Preview Label';
    setLabelPreviewTitle(title);
    setShowLabelPreviewModal(true);
    setLabelPreviewLoading(true);
    setLabelPreviewError(null);
    setLabelPreviewImages([]);

    try {
      const result = await previewLabel({ kind: 'warehouse', items });
      setLabelPreviewImages(result.images);
    } catch (error: unknown) {
      console.error('Error previewing warehouse label:', error);
      setLabelPreviewError(getApiErrorMessage(error) || 'Failed to preview label');
    } finally {
      setLabelPreviewLoading(false);
    }
  };

  const handlePreviewBigLabel = async () => {
    const { SKU: currentSKU, baseQty: currentBaseQty } = inputsRef.current;
    if (!currentSKU || labelPreviewLoading) {
      return;
    }

    setLabelPreviewTitle('Preview Big Label');
    setShowLabelPreviewModal(true);
    setLabelPreviewLoading(true);
    setLabelPreviewError(null);
    setLabelPreviewImages([]);

    try {
      const result = await previewLabel({
        kind: 'big',
        sku: currentSKU,
        baseQty: currentBaseQty || null,
      });
      setLabelPreviewImages(result.images);
    } catch (error: unknown) {
      console.error('Error previewing big label:', error);
      setLabelPreviewError(getApiErrorMessage(error) || 'Failed to preview big label');
    } finally {
      setLabelPreviewLoading(false);
    }
  };

  const handlePrintAisle = async () => {
    const { shelfIndex: currentShelfIndex } = inputsRef.current;
    if (!currentShelfIndex || isSubmitting) return;

    const parts = String(currentShelfIndex).trim().split('-');
    if (parts.length < 3) {
      setAisleRangeError('Shelf index must be in format number-letter-number (e.g. 5-A-1).');
      return;
    }

    if (!aisleLetterStart || !aisleLetterEnd) {
      setAisleRangeError('Please select both start and end letters.');
      return;
    }

    const startIdx = aisleLetterStart.charCodeAt(0) - 65;
    const endIdx = aisleLetterEnd.charCodeAt(0) - 65;
    if (startIdx > endIdx) {
      setAisleRangeError('Start letter must be earlier than or equal to end letter.');
      return;
    }

    setAisleRangeError('');
    setShowPrintAisleModal(false);

    const aisleNum = parts[0];
    const letters = Array.from({ length: endIdx - startIdx + 1 }, (_, i) =>
      String.fromCharCode(65 + startIdx + i)
    );
    const aisleShelves = letters.flatMap((letter) =>
      SHELF_RANGE.POSITIONS_1_TO_6.map((n) => `${aisleNum}-${letter}-${n}`)
    );

    await printShelfUnits(
      aisleShelves,
      `Printing aisle ${aisleNum} columns ${letters[0]}-${letters[letters.length - 1]} (${aisleShelves.length} labels)...`,
    );
  };

  const handlePrintBigLabel = async (copies = 1) => {
    const {
      SKU: currentSKU,
      shelfIndex: currentShelfIndex,
      labelConfig: currentLabelConfig,
      baseQty: currentBaseQty,
      uniqueIdCount: currentUniqueIdCount,
    } = inputsRef.current;

    if (isSubmitting) {
      return;
    }

    if (!currentSKU) {
      setMessage('SKU is required to print big label.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    if (!isWarehouseSkuLabelType(selectedType)) {
      setMessage('Select Stock or Pick Bin before printing.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    const nUnique = clampUniqueIds(currentUniqueIdCount);
    const totalLabels = nUnique * copies;
    const baseQtyValue = parseBaseQty(currentBaseQty);
    const units: PrintOrderUnitInput[] = Array.from({ length: nUnique }, () => ({
      sku: currentSKU,
      shelfIndex: currentShelfIndex || null,
      baseQty: baseQtyValue,
    }));

    await runPrint(
      {
        labelKind: 'big',
        labelType: toWarehouseLabelType(currentLabelConfig?.type),
        source,
        copiesPerId: copies,
        units,
      },
      totalLabels > 1
        ? `Printing ${totalLabels} big labels (${nUnique} unique ID${nUnique !== 1 ? 's' : ''})...`
        : 'Printing big label...',
      'big label',
    );
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const {
      SKU: currentSKU,
      shelfIndex: currentShelfIndex,
      labelConfig: currentLabelConfig,
      printBatch: currentPrintBatch,
      printLabelCount: currentPrintLabelCount,
      uniqueIdCount: currentUniqueIdCount,
    } = inputsRef.current;

    if (selectedType === 'shelf') {
      const shelvesToPrint = currentPrintBatch.length > 0
        ? currentPrintBatch
            .map((item) => item.shelfIndex)
            .filter((shelf): shelf is string => Boolean(shelf))
        : currentShelfIndex
          ? [currentShelfIndex]
          : [];

      if (shelvesToPrint.length === 0) {
        setMessage('Shelf is required to print label.');
        setTimeout(() => setMessage(''), 3000);
        return;
      }

      const printed = await printShelfUnits(
        shelvesToPrint,
        shelvesToPrint.length > 1
          ? `Printing ${shelvesToPrint.length} shelf labels...`
          : 'Printing shelf label...'
      );
      if (printed) {
        setPrintBatch([]);
      }
      return;
    }

    if (!isWarehouseSkuLabelType(selectedType)) {
      setMessage('Select Stock or Pick Bin before printing.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    const isBatchPrint = currentPrintBatch.length > 0;
    const nUnique = isBatchPrint ? currentPrintBatch.length : clampUniqueIds(currentUniqueIdCount);
    const copies = isBatchPrint ? 1 : currentPrintLabelCount;

    const units: PrintOrderUnitInput[] = isBatchPrint
      ? currentPrintBatch.map((item) => ({
        sku: item.sku ?? '',
        shelfIndex: item.shelfIndex,
      }))
      : Array.from({ length: nUnique }, () => ({
        sku: currentSKU,
        shelfIndex: currentShelfIndex || null,
      }));

    if (units.length === 0 || !units[0].sku) {
      setMessage('SKU is required to print label.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    const totalLabels = nUnique * copies;
    const printed = await runPrint(
      {
        labelKind: 'warehouse',
        labelType: toWarehouseLabelType(currentLabelConfig?.type),
        source,
        copiesPerId: copies,
        units,
      },
      isBatchPrint
        ? `Printing ${units.length} labels...`
        : totalLabels > 1
          ? `Printing ${totalLabels} labels (${nUnique} unique ID${nUnique !== 1 ? 's' : ''})...`
          : 'Printing label...'
    );

    if (printed) {
      setPrintBatch([]);
    }
  };

  const numbers1to10 = SHELF_RANGE.NUMBERS_1_TO_10;
  const lettersAtoZ = SHELF_RANGE.LETTERS_A_TO_Z;
  const numbers1to6 = SHELF_RANGE.POSITIONS_1_TO_6;

  const printJobIdRows = lastPrintJob
    ? aggregatePrintJobIds(flattenPrintOrderItems(lastPrintJob.sheets))
    : [];
  const printJobLabelCount = printJobIdRows.reduce((sum, row) => sum + row.count, 0);

  const showPrintEventBanner = Boolean(lastPrintJob && printJobIdRows.length > 0);

  return (
    <div
      className={`pbl-container${embedded ? ' pbl-container-modal' : ''}${
        showPrintEventBanner ? ' pbl-has-print-event-banner' : ''
      }`}
    >
      {embedded ? (
        <h1 className="pbl-title">{navbarTitleForPathname('/print-warehouse-labels/print')}</h1>
      ) : null}
      {showPrintEventBanner ? (
        <div className="pbl-print-event-banner">
          <div className="pbl-print-event-banner-inner">
            <button
              type="button"
              className="pbl-print-event-banner-hit"
              onClick={() => setPrintEventHandlingOpen(true)}
            >
              <div className="pbl-print-event-banner-title">Print order sent</div>
              <div className="pbl-print-event-banner-counts">
                {formatPrintJobBannerCounts(printJobIdRows.length, printJobLabelCount)}
              </div>
            </button>
            <ModalBackButton
              direction="forward"
              ariaLabel="Open print event handling"
              onClick={() => setPrintEventHandlingOpen(true)}
            />
          </div>
        </div>
      ) : null}
      {/* Reserved band between the navbar H1 and Label Type so success/error copy
          never shifts the form. Same slot as Shelf Indexer / Search Warehouse. */}
      <div className="pbl-page-header-status" role="status" aria-live="polite">
        {message ? (
          <div className={printStatusSlotClass(isErrorMessage, errorInvalidInput, message)}>
            {errorInvalidInput && message.includes(errorInvalidInput) ? (
              <>
                {message.split(errorInvalidInput)[0]}
                <span className="pbl-message-invalid">{errorInvalidInput}</span>
                {message.split(errorInvalidInput).slice(1).join(errorInvalidInput)}
              </>
            ) : (
              message
            )}
          </div>
        ) : null}
      </div>

      {(skusLoading || shelvesLoading) && (
        <div className="pbl-loading">
          <div className="pbl-loading-spinner"></div>
          <div className="pbl-loading-text">Loading databases...</div>
        </div>
      )}

      <div className="pbl-section">
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">Label Type</span>
        </div>
        <div className="pbl-info-display">
          <div className="pbl-info-row">
            <span className="pbl-info-label">Type:</span>
            <div className="pbl-info-value-wrapper">
              <span
                className={`pbl-info-value pbl-info-clickable ${!selectedType ? 'pbl-info-empty' : ''}`}
                onClick={() => setShowTypeSelector(true)}
              >
                {LABEL_TYPE_OPTIONS.find((option) => option.value === selectedType)?.label ?? 'Select type'}
              </span>
              {selectedType && (
                <button
                  className="pbl-info-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedType('');
                    setShelfIndex('');
                    setEndShelfIndex('');
                    setBaseQty('');
                    resetUniqueIdsToDefault();
                    setPrintBatch([]);
                    setErrorInvalidInput(null);
                    setMessage('Type cleared');
                    setTimeout(() => setMessage(''), 2000);
                  }}
                  title="Clear Type"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showTypeSelector && (
        <div className="pbl-modal-overlay" onClick={() => setShowTypeSelector(false)}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pbl-modal-close" onClick={() => setShowTypeSelector(false)}>×</button>
            <div className="pbl-modal-title">Select Type</div>
            <div className="pbl-manual-popup">
              {LABEL_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className="pbl-type-option"
                  onClick={() => selectLabelType(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedType !== 'shelf' && selectedType !== 'reprint' && (
        <div className={`pbl-section ${!selectedType ? 'pbl-section-disabled' : ''}`}>
          <div className="pbl-section-title">
            <span className="pbl-section-text">Scanner</span>
          </div>
          <ScannerSection mode="barcode" onCapture={handleCapture} className="webcam-capture-container" />
        </div>
      )}

      {selectedType !== 'reprint' && (
      <div className={`pbl-section ${!selectedType ? 'pbl-section-disabled' : ''}`}>
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">Label Details</span>
        </div>
        {selectedType === 'shelf' ? (
          <div className="pbl-info-display">
            <div className="pbl-info-row">
              <span className="pbl-info-label">Shelf:</span>
              <div className="pbl-info-value-wrapper">
                <span
                  className={`pbl-info-value pbl-info-clickable ${!shelfIndex ? 'pbl-info-empty' : ''}`}
                  onClick={() => openShelfModal('start')}
                >
                  {shelfIndex || 'Select shelf index'}
                </span>
                {shelfIndex && (
                  <button
                    className="pbl-info-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShelfIndex('');
                      setEndShelfIndex('');
                      setMessage('Shelf cleared');
                      setTimeout(() => setMessage(''), 2000);
                    }}
                    title="Clear Shelf"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="pbl-info-row">
              <span className="pbl-info-label">End shelf:</span>
              <div className="pbl-info-value-wrapper">
                <span
                  className={`pbl-info-value pbl-info-clickable ${!endShelfIndex ? 'pbl-info-empty' : ''}`}
                  onClick={() => openShelfModal('end')}
                >
                  {endShelfIndex || 'Optional'}
                </span>
                {endShelfIndex && (
                  <button
                    className="pbl-info-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEndShelfIndex('');
                      setMessage('End shelf cleared');
                      setTimeout(() => setMessage(''), 2000);
                    }}
                    title="Clear End shelf"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
        <div className="pbl-info-display">
          {labelConfig?.skuRequired && (
            <div className="pbl-info-row">
              <span className="pbl-info-label">SKU:</span>
              <div className="pbl-info-value-wrapper">
                <span 
                  className={`pbl-info-value pbl-info-clickable ${!SKU ? 'pbl-info-empty' : ''}`}
                  onClick={() => {
                    setShowManualSKUInput(true);
                    setShowManualShelfInput(false);
                    setShowManualNameInput(false);
                    setManualSKU(SKU || SKU_PREFIX);
                    setTimeout(() => {
                      const modal = document.querySelector('.pbl-modal-overlay');
                      if (modal) {
                        modal.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                      if (skuInputRef.current) {
                        skuInputRef.current.focus();
                        const length = skuInputRef.current.value.length;
                        skuInputRef.current.setSelectionRange(length, length);
                      }
                    }, 100);
                  }}
                >
                  {SKU || 'Scan or enter manually'}
                </span>
                {SKU || productName ? (
                  <button
                    className="pbl-info-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearProductCriteria();
                      setMessage('SKU cleared');
                      setTimeout(() => setMessage(''), 2000);
                    }}
                    title="Clear SKU"
                    aria-label="Clear SKU"
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </div>
          )}
          {labelConfig?.skuRequired && (
            <div className="pbl-info-row">
              <span className="pbl-info-label">Name:</span>
              <div className="pbl-info-value-wrapper">
                <span
                  className={`pbl-info-value pbl-info-clickable ${!productName ? 'pbl-info-empty' : ''}`}
                  onClick={() => {
                    setShowManualNameInput(true);
                    setShowManualSKUInput(false);
                    setShowManualShelfInput(false);
                    setTimeout(() => {
                      const modal = document.querySelector('.pbl-modal-overlay');
                      if (modal) {
                        modal.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                >
                  {productName || 'Search by product or variant name'}
                </span>
                {SKU || productName ? (
                  <button
                    className="pbl-info-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearProductCriteria();
                      setMessage('Name cleared');
                      setTimeout(() => setMessage(''), 2000);
                    }}
                    title="Clear Name"
                    aria-label="Clear Name"
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </div>
          )}
          {(labelConfig?.shelfRequired || labelConfig?.shelfOptional) && (
            <div className="pbl-info-row">
              <span className="pbl-info-label">Shelf:</span>
              <div className="pbl-info-value-wrapper">
                <span 
                  className={`pbl-info-value pbl-info-clickable ${!shelfIndex ? 'pbl-info-empty' : ''}`}
                  onClick={() => openShelfModal('start')}
                >
                  {shelfIndex || (labelConfig?.shelfOptional ? 'Optional - scan or enter manually' : 'Scan or enter manually')}
                </span>
                {shelfIndex && (
                  <button
                    className="pbl-info-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShelfIndex('');
                      setMessage('Shelf cleared');
                      setTimeout(() => setMessage(''), 2000);
                    }}
                    title="Clear Shelf"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          )}
          {labelConfig?.showBaseQty && (
            <div className="pbl-info-row">
              <span className="pbl-info-label">Base qty:</span>
              <div className="pbl-info-value-wrapper">
                <input
                  type="text"
                  value={baseQty}
                  onChange={(e) => setBaseQty(e.target.value)}
                  placeholder="Optional - enter manually"
                  className="pbl-base-qty-input"
                />
                {baseQty && (
                  <button
                    className="pbl-info-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBaseQty('');
                      setMessage('Base qty cleared');
                      setTimeout(() => setMessage(''), 2000);
                    }}
                    title="Clear Base qty"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          )}
          {labelConfig && labelConfig.type !== 'shelf' && (
            <div className="pbl-info-row">
              <span className="pbl-info-label">Unique ID&apos;s:</span>
              <div className="pbl-info-value-wrapper">
                <input
                  type="text"
                  inputMode="numeric"
                  role="spinbutton"
                  value={formatUniqueIdDisplay(uniqueIdCount, uniqueIdInputFocused, uniqueIdInputStr)}
                  onFocus={() => {
                    setUniqueIdInputFocused(true);
                    setUniqueIdInputStr('');
                  }}
                  onBlur={() => {
                    const trimmed = uniqueIdInputStr.trim();
                    const next =
                      trimmed === '' || Number.isNaN(parseInt(trimmed, 10))
                        ? UNIQUE_IDS_MIN
                        : clampUniqueIds(parseInt(trimmed, 10));
                    setUniqueIdCount(next);
                    setUniqueIdInputStr(String(next));
                    setUniqueIdInputFocused(false);
                  }}
                  onChange={(e) => setUniqueIdInputStr(e.target.value)}
                  className="pbl-base-qty-input"
                  aria-label="Number of unique box storage IDs"
                  aria-valuemin={UNIQUE_IDS_MIN}
                  aria-valuemax={UNIQUE_IDS_MAX}
                />
                {effectiveUniqueIdCountValue > UNIQUE_IDS_MIN ? (
                  <button
                    type="button"
                    className="pbl-info-clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      resetUniqueIdsToDefault();
                      setMessage("Unique ID's cleared");
                      setTimeout(() => setMessage(''), 2000);
                    }}
                    title="Clear Unique ID's"
                    aria-label="Clear Unique ID's"
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      )}

      <div className={`pbl-section ${!selectedType ? 'pbl-section-disabled' : ''}`}>
        <div className="pbl-section-title pbl-section-info">
          <span className="pbl-section-text">Destination Printer</span>
        </div>
        <div className="pbl-destination-toggle" role="group" aria-label="Destination printer">
          {DESTINATION_PRINTERS.map((printer) => (
            <button
              key={printer.id}
              type="button"
              className={`pbl-destination-toggle-btn${printerId === printer.id ? ' pbl-destination-toggle-btn-active' : ''}`}
              aria-pressed={printerId === printer.id}
              onClick={() => setPrinterId(printer.id)}
            >
              {printer.label}
            </button>
          ))}
        </div>
      </div>

      {selectedType === 'reprint' && (
        <ReprintLabelPanel
          source={source}
          printerId={printerId}
          initialStorageIdPrefix={queryReprintPrefix ?? undefined}
        />
      )}

      {showManualShelfInput && (labelConfig?.shelfRequired || labelConfig?.shelfOptional || selectedType === 'shelf') && (
        <div className="pbl-modal-overlay" onClick={handleManualShelfInputClose}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="pbl-modal-close"
              onClick={handleManualShelfInputClose}
            >
              ×
            </button>
            <div className="pbl-modal-title">
              {shelfModalTarget === 'end' ? 'Enter End Shelf Index' : 'Enter Shelf Index'}
            </div>
            <div className="pbl-manual-popup">
              <div className="pbl-dropdown-container">
                <select
                  value={shelfNumber}
                  onChange={(e) => { setShelfNumber(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {numbers1to10.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>

                <span className="pbl-separator">-</span>

                <select
                  value={shelfLetter}
                  onChange={(e) => { setShelfLetter(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {lettersAtoZ.map(letter => (
                    <option key={letter} value={letter}>{letter}</option>
                  ))}
                </select>

                <span className="pbl-separator">-</span>

                <select
                  value={shelfPosition}
                  onChange={(e) => { setShelfPosition(e.target.value); setManualShelfText(''); }}
                  className="pbl-dropdown"
                >
                  <option value="">--</option>
                  {numbers1to6.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>

              <div className="pbl-or-divider">or</div>
              <ShelfAutocompleteInput
                value={manualShelfText}
                onChange={(val) => { setManualShelfText(val); setShelfNumber(''); setShelfLetter(''); setShelfPosition(''); }}
                onSelect={(shelf) => { setManualShelfText(shelf); setShelfNumber(''); setShelfLetter(''); setShelfPosition(''); }}
                placeholder="Type shelf name"
              />

              {shelfModalError && (
                <div className="pbl-message" style={{ color: '#ff6b6b' }}>{shelfModalError}</div>
              )}
              <button
                onClick={handleManualShelfInput}
                className="pbl-submit-button"
                disabled={(!shelfNumber || !shelfLetter || !shelfPosition) && !manualShelfText.trim()}
              >
                Set Shelf Index
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualSKUInput && labelConfig?.skuRequired && (
        <div className="pbl-modal-overlay" onClick={handleManualSKUInputClose}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="pbl-modal-close"
              onClick={handleManualSKUInputClose}
            >
              ×
            </button>
            <div className="pbl-modal-title">Enter SKU</div>
            <div className="pbl-manual-popup">
              <SkuAutocompleteInput
                inputRef={skuInputRef}
                autocompleteRef={autocompleteRef}
                value={manualSKU}
                onChange={handleSKUInputChange}
                onKeyDown={handleSKUInputKeyDown}
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter' && selectedIndex < 0) {
                    handleManualSKUInput();
                  }
                }}
                placeholder={`${SKU_PREFIX}...`}
                showAutocomplete={showAutocomplete}
                filteredSkus={filteredSKUs}
                selectedIndex={selectedIndex}
                onSelectSku={selectSKU}
                onHoverIndex={setSelectedIndex}
              />

              <button
                onClick={handleManualSKUInput}
                className="pbl-submit-button"
                disabled={!manualSKU.trim() || manualSKU.trim().length <= SKU_PREFIX.length}
              >
                Set SKU
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductNameSearchModal
        open={showManualNameInput && Boolean(labelConfig?.skuRequired)}
        initialQuery={productName}
        onClose={() => setShowManualNameInput(false)}
        onSelect={selectNameResult}
      />

      {selectedType === 'shelf' && (shelfIndex || printBatch.length > 0) && (
        <div className="pbl-submit-section">
          <div className="pbl-section-title pbl-section-info">
            <span className="pbl-section-text">Print Shelf Labels</span>
          </div>
          <div className="pbl-print-buttons">
            {shelfRangeMode ? (
              <>
                {shelfRangeError && (
                  <div className="pbl-aisle-error">{shelfRangeError}</div>
                )}
                <button
                  onClick={() => { void handlePrintShelfRange(); }}
                  disabled={isSubmitting || shelfRangeError != null}
                  className="pbl-print-button"
                  type="button"
                >
                  {isSubmitting ? 'Printing...' : 'Print Labels'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleAddToBatch}
                  disabled={isSubmitting || printBatch.length >= SHELF_LABEL_BATCH_MAX || !shelfIndex}
                  className="pbl-add-to-batch-button"
                >
                  {printBatch.length >= SHELF_LABEL_BATCH_MAX ? `Print batch full (${SHELF_LABEL_BATCH_MAX})` : 'Add to Print Batch'}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="pbl-print-button"
                >
                  {isSubmitting
                    ? (printBatch.length > 0 ? 'Printing Batch...' : 'Printing...')
                    : (printBatch.length > 0 ? `Batch Print Labels (${printBatch.length})` : 'Print Label')}
                </button>
                <button
                  onClick={handlePrintColumn}
                  disabled={isSubmitting || !shelfIndex}
                  className="pbl-print-big-button"
                  type="button"
                >
                  Print Column
                </button>
                <button
                  onClick={handleOpenPrintAisleModal}
                  disabled={isSubmitting || !shelfIndex}
                  className="pbl-print-aisle-button"
                  type="button"
                >
                  Print Aisle
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showPrintAisleModal && shelfIndex && (
        <div className="pbl-modal-overlay" onClick={handleClosePrintAisleModal}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pbl-modal-close" onClick={handleClosePrintAisleModal}>×</button>
            <div className="pbl-modal-title">
              Select Column Range for Aisle {String(shelfIndex).trim().split('-')[0] || '?'}
            </div>
            <div className="pbl-manual-popup">
              <div className="pbl-aisle-range-row">
                <select
                  value={aisleLetterStart}
                  onChange={(e) => {
                    setAisleLetterStart(e.target.value);
                    setAisleRangeError('');
                  }}
                  className="pbl-dropdown pbl-aisle-dropdown"
                >
                  <option value="">--</option>
                  {lettersAtoZ.map((letter) => (
                    <option key={letter} value={letter}>{letter}</option>
                  ))}
                </select>
                <span className="pbl-separator">to</span>
                <select
                  value={aisleLetterEnd}
                  onChange={(e) => {
                    setAisleLetterEnd(e.target.value);
                    setAisleRangeError('');
                  }}
                  className="pbl-dropdown pbl-aisle-dropdown"
                >
                  <option value="">--</option>
                  {lettersAtoZ.map((letter) => (
                    <option key={letter} value={letter}>{letter}</option>
                  ))}
                </select>
              </div>
              {(aisleRangeError || (aisleLetterStart && aisleLetterEnd && aisleLetterStart.charCodeAt(0) > aisleLetterEnd.charCodeAt(0))) && (
                <div className="pbl-aisle-error">
                  {aisleRangeError || 'Start letter must be earlier than or equal to end letter.'}
                </div>
              )}
              {aisleLetterStart && aisleLetterEnd && (
                <button
                  onClick={handlePrintAisle}
                  disabled={aisleLetterStart.charCodeAt(0) > aisleLetterEnd.charCodeAt(0)}
                  className="pbl-print-aisle-confirm"
                >
                  Print Aisle
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {(SKU || printBatch.length > 0) && isWarehouseSkuPrintType && (
        <div className="pbl-submit-section">
          {hasInputForPrint && (
            <div className="pbl-section-title pbl-section-info">
              <span className="pbl-section-text">
                {selectedType === 'stock' ? 'Print Stock Labels' : 'Print Pick Bin Labels'}
              </span>
            </div>
          )}
          <div className="pbl-print-buttons">
            <div className="pbl-count-row">
              <button type="button" className="pbl-increment-btn pbl-increment-spacer" aria-hidden="true" tabIndex={-1} disabled />
              <button
                onClick={handleAddToBatch}
                disabled={
                  isSubmitting
                  || printBatch.length >= getMaxBatchSize()
                  || !SKU
                  || SKU === lastAddedSku
                  || effectiveUniqueIdCountValue > 1
                }
                className={`pbl-add-to-batch-button pbl-print-button-in-row ${SKU === lastAddedSku ? 'pbl-add-to-batch-locked' : ''}`}
              >
                {printBatch.length >= getMaxBatchSize() ? `Print batch full (${getMaxBatchSize()})` : 'Add to Print Batch'}
              </button>
              <button type="button" className="pbl-increment-btn pbl-increment-spacer" aria-hidden="true" tabIndex={-1} disabled />
            </div>
            {printBatch.length > 0 ? (
              <div className="pbl-count-row">
                <button type="button" className="pbl-increment-btn pbl-increment-spacer" aria-hidden="true" tabIndex={-1} disabled />
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="pbl-print-button pbl-print-button-in-row"
                >
                  {isSubmitting ? 'Printing Batch...' : `Batch Print Labels (${printBatch.length})`}
                </button>
                <button type="button" className="pbl-increment-btn pbl-increment-spacer" aria-hidden="true" tabIndex={-1} disabled />
              </div>
            ) : (
              <>
                <div className="pbl-count-row">
                  <button
                    type="button"
                    className="pbl-increment-btn"
                    onClick={() => setPrintLabelCount(c => Math.max(1, c - 1))}
                    disabled={isSubmitting || printLabelCount <= 1}
                    aria-label="Decrease label count"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !SKU}
                    className="pbl-print-button pbl-print-button-in-row"
                  >
                    {isSubmitting ? 'Printing...' : `Print ${printLabelCount} Label${printLabelCount !== 1 ? 's' : ''}`}
                  </button>
                  <button
                    type="button"
                    className="pbl-increment-btn"
                    onClick={() => setPrintLabelCount(c => Math.min(8, c + 1))}
                    disabled={isSubmitting || printLabelCount >= 8}
                    aria-label="Increase label count"
                  >
                    +
                  </button>
                </div>
                <div className="pbl-count-row">
                  <button
                    type="button"
                    className="pbl-increment-btn"
                    onClick={() => setPrintBigLabelCount(c => Math.max(1, c - 1))}
                    disabled={isSubmitting || !SKU || printBigLabelCount <= 1}
                    aria-label="Decrease big label count"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePrintBigLabel(printBigLabelCount); }}
                    disabled={isSubmitting || !SKU}
                    className="pbl-print-big-button pbl-print-button-in-row"
                  >
                    {isSubmitting ? 'Printing...' : `Print ${printBigLabelCount} Big Label${printBigLabelCount !== 1 ? 's' : ''}`}
                  </button>
                  <button
                    type="button"
                    className="pbl-increment-btn"
                    onClick={() => setPrintBigLabelCount(c => Math.min(8, c + 1))}
                    disabled={isSubmitting || printBigLabelCount >= 8}
                    aria-label="Increase big label count"
                  >
                    +
                  </button>
                </div>
              </>
            )}
            {isDev && (
              <>
                <div className="pbl-count-row">
                  <button type="button" className="pbl-increment-btn pbl-increment-spacer" aria-hidden="true" tabIndex={-1} disabled />
                  <button
                    type="button"
                    onClick={() => { void handlePreviewWarehouseLabel(); }}
                    disabled={
                      labelPreviewLoading
                      || isSubmitting
                      || (printBatch.length === 0 && !SKU)
                    }
                    className="pbl-preview-label-button pbl-print-button-in-row"
                  >
                    {printBatch.length > 0 ? 'Preview Print Batch' : 'Preview Label'}
                  </button>
                  <button type="button" className="pbl-increment-btn pbl-increment-spacer" aria-hidden="true" tabIndex={-1} disabled />
                </div>
                {printBatch.length === 0 && (
                  <div className="pbl-count-row">
                    <button type="button" className="pbl-increment-btn pbl-increment-spacer" aria-hidden="true" tabIndex={-1} disabled />
                    <button
                      type="button"
                      onClick={() => { void handlePreviewBigLabel(); }}
                      disabled={labelPreviewLoading || isSubmitting || !SKU}
                      className="pbl-preview-big-label-button pbl-print-button-in-row"
                    >
                      Preview Big label
                    </button>
                    <button type="button" className="pbl-increment-btn pbl-increment-spacer" aria-hidden="true" tabIndex={-1} disabled />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {printBatch.length > 0 && !shelfRangeMode && (
        <div className="pbl-section">
          <div className="pbl-section-title">
            <span className="pbl-section-text">
              Print Batch ({printBatch.length}/{selectedType === 'shelf' ? SHELF_LABEL_BATCH_MAX : 5})
            </span>
          </div>
          <div className="pbl-batch-list">
            {printBatch.map((item, index) => (
              <div key={index} className="pbl-batch-item">
                {item.labelType === 'shelf' ? (
                  <span className="pbl-batch-sku">{item.shelfIndex}</span>
                ) : (
                  <>
                    <span className="pbl-batch-sku">{item.sku}</span>
                    {item.shelfIndex ? (
                      <span className="pbl-batch-shelf">{item.shelfIndex}</span>
                    ) : (
                      <span className="pbl-batch-shelf pbl-batch-no-shelf">No Shelf</span>
                    )}
                  </>
                )}
                <button
                  onClick={() => {
                    const newBatch = printBatch.filter((_, i) => i !== index);
                    setPrintBatch(newBatch);
                    setMessage(`Removed ${item.sku || item.shelfIndex} from batch`);
                    setTimeout(() => setMessage(''), 2000);
                  }}
                  className="pbl-batch-remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showLabelPreviewModal && (
        <div className="pbl-modal-overlay" onClick={handleCloseLabelPreviewModal}>
          <div className="pbl-modal pbl-label-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="pbl-modal-close"
              onClick={handleCloseLabelPreviewModal}
              aria-label="Close preview"
            >
              ×
            </button>
            <div className="pbl-modal-title">{labelPreviewTitle || 'Label Preview'}</div>
            {labelPreviewLoading && (
              <div className="pbl-loading">
                <div className="pbl-loading-spinner" />
                <div className="pbl-loading-text">Rendering preview...</div>
              </div>
            )}
            {labelPreviewError && (
              <div className="pbl-message pbl-message-error">{labelPreviewError}</div>
            )}
            {!labelPreviewLoading && !labelPreviewError && labelPreviewImages.length > 0 && (
              <div className="pbl-label-preview-images">
                {labelPreviewImages.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`Label preview sheet ${index + 1}`}
                    className="pbl-label-preview-image"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {lastPrintJob ? (
        <PrintEventHandlingModal
          key={`${lastPrintJob.order.id}:${resumedScannedPrefix ?? ''}`}
          isOpen={printEventHandlingOpen}
          onClose={() => setPrintEventHandlingOpen(false)}
          job={lastPrintJob}
          source={source}
          printerId={printerId}
          initialScannedPrefix={resumedScannedPrefix}
        />
      ) : null}
    </div>
  );
}

export default PrintBoxLabel;
